<script setup>
  import {computed, inject, nextTick, ref} from "vue";

  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentCommentList from "@components/Lists/CommentList.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {MedButton, MedInfoButton, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PagesRecordResultListClaimInteractionChat",
  });

  const props = defineProps({
    patientId: {
      type: String,
      required: true,
    },
    recordId: {
      type: String,
      required: true,
    },
    comments: {
      type: Array,
      required: true,
    },
    commentSection: {
      type: String,
      required: true,
    },
    hideShare: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["open:commentsDialog", "open:shareDialog"]);

  const sharedWithMe = inject("sharedWithMe");

  const editAuthor = ref(false);
  const refAuthorInput = ref(null);

  const pageProperties = usePage().props;

  const form = useForm({
    area: props.commentSection === "default" ? "default" : "result",
    section: props.commentSection,
    content: null,
    author: pageProperties.client.company
      ? pageProperties.client.company +
        " (" +
        (pageProperties.client.first_name ?? "") +
        " " +
        (pageProperties.client.last_name ?? "") +
        ")".trim()
      : ((pageProperties.client.first_name ?? "") + " " + (pageProperties.client.last_name ?? "")).trim(),
  });

  function save() {
    form.post(route("comments.store", {patient: props.patientId, record: props.recordId}), {
      async: true,
      // Inertia cancels other in-flight async visits whenever a visit targets a different URL than
      // the current page (true for every form submit here). Only optimistic visits are exempt, so
      // this no-op optimistic callback keeps parallel chat instances from cancelling one another.
      optimistic: () => undefined,
      preserveScroll: true,
      only: ["flash", "errors", "comments", "notifications", "checks"],
      onSuccess: () => {
        form.reset("content");
      },
    });
  }

  const showShareButton = computed(() => {
    return (
      !props.hideShare &&
      pageProperties.client.license.is_pro &&
      ((sharedWithMe.value && pageProperties?.can?.allow_shared_document_forwarding) || !sharedWithMe.value)
    );
  });

  const clickAuthorInput = () => {
    editAuthor.value = true;

    nextTick(() => {
      refAuthorInput.value?.focus();
    });
  };
</script>

<template>
  <div class="mb-4 flex items-center space-x-2">
    <h6 class="cursor-default text-sm font-semibold">
      Kommentare
      <template v-if="$page.props.client.license.is_pro">(Kollegen / Fallfreigabe)</template>
    </h6>

    <MedInfoButton
      size="lg"
      side="top"
      aria-label="Kommentare Info"
      :data-test="getTestId('interaction-chat-info-button')"
    >
      <p class="max-w-sm text-sm">
        Kommentare werden in Echtzeit für diejenigen MediCheck-Nutzer sichtbar, die Zugriff auf diese Variante besitzen.
      </p>
      <p class="mt-2 max-w-sm text-sm font-semibold">
        Hinweis: Kommentare gehen nicht in die Arzt- oder Patientenkommunikation ein.
      </p>
    </MedInfoButton>
  </div>

  <ComponentCommentList
    :patient-id="patientId"
    :record-id="recordId"
    :tight="true"
    :comments="comments"
    :test-id="getTestId('interaction')"
    @show-all="$emit('open:commentsDialog')"
  />

  <form class="mt-8" @submit.prevent="save()">
    <MedTooltip content="Sie kommentieren mit diesem Namen">
      <template v-if="!editAuthor">
        <ComponentInput v-model="form.author" hide-clear @click="clickAuthorInput" />
      </template>

      <template v-else>
        <ComponentInput
          ref="refAuthorInput"
          v-model="form.author"
          placeholder="Bitte HIER Ihren Namen eintragen"
          :validation="form.errors.author"
          :test-id="getTestId('interaction-input-author')"
          @blur="editAuthor = false"
        />
      </template>
    </MedTooltip>

    <ComponentTextarea
      v-model="form.content"
      class="mt-2"
      placeholder="Kommentar"
      rows="2"
      :validation="form.errors.content"
    />

    <div class="mt-1 flex justify-end">
      <MedButton
        type="submit"
        variant="primary"
        flat
        icon="mode_comment"
        :disabled="form.processing"
        :data-test="getTestId('interaction-save-button')"
      >
        Speichern
      </MedButton>
    </div>
  </form>

  <div v-if="showShareButton" class="mt-6 border-t border-gray-200 pt-2">
    <p class="text-sm">Möchten Sie diese Variante mit Kollegen besprechen?</p>

    <div class="flex justify-end">
      <MedButton
        variant="primary"
        flat
        icon="share"
        :data-test="getTestId('interaction-open-share-dialog-button')"
        @click="$emit('open:shareDialog')"
      >
        Fallfreigabe
      </MedButton>
    </div>
  </div>
</template>
